import { Component, createSignal, onMount, Switch, Match } from "solid-js";
import { FsModalDialog } from "@cof/fs-modal-dialog-wc";
import { FsTextField } from "@cof/fs-text-field-wc";
import { sendSignedAgreementEmail } from "@/services/eligibility.service";
import {
  maskEmail,
  sanitizeEmail,
  sanitizeName,
} from "@/utils/parsedata.utils";
import {
  publishAgreementsModalView,
  publishAgreementsNameInputChange,
  publishAgreementsEmailInputChange,
  publishAgreementsBackClick,
  publishAgreementsSendEmailClick,
  publishAgreementsTryAnotherEmailClick,
  publishAgreementsResendEmailClick,
} from "@/services/analytics.service";

interface AgreementsModalProps {
  dealerId: string;
  onClose: (modalRef: FsModalDialog) => void;
}

type ModalView = "form" | "confirmation" | "resent";

export const AgreementsModal: Component<AgreementsModalProps> = (props) => {
  let modalRef: FsModalDialog | undefined;
  const [view, setView] = createSignal<ModalView>("form");
  const [name, setName] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [nameValid, setNameValid] = createSignal(false);
  const [emailValid, setEmailValid] = createSignal(false);

  onMount(() => {
    publishAgreementsModalView();
  });

  // checkValidity() isn't properly working with solidjs signals
  // results in either the the submit button being enabled when it shouldn't and all fields invalidated when a single field is focused
  const isFormValid = () => nameValid() && emailValid();

  const handleNameInput = (e: InputEvent) => {
    const target = e.target as FsTextField;
    setName(target.value);
    setNameValid(target.validity?.valid ?? false);
  };

  const handleEmailInput = (e: InputEvent) => {
    const target = e.target as FsTextField;
    setEmail(target.value);
    setEmailValid(target.validity?.valid ?? false);
  };

  const onSubmitForm = () => {
    publishAgreementsSendEmailClick();
    sendEmail();
    setView("confirmation");
  };

  const onTryAnotherEmail = () => {
    publishAgreementsTryAnotherEmailClick();
    setEmail("");
    setEmailValid(false);
    setView("form");
  };

  const onResendEmail = () => {
    publishAgreementsResendEmailClick();
    sendEmail();
    setView("resent");
  };

  const sendEmail = async () => {
    try {
      await sendSignedAgreementEmail(props.dealerId, {
        dealerContactFullName: sanitizeName(name()),
        dealerContactEmail: sanitizeEmail(email()),
      });
    } catch (error) {
      // TODO - placeholder for error handling modal
      console.error("Failed to send signed agreement email:", error);
    }
  };

  return (
    <fd-mfe-modal-dialog
      ref={modalRef}
      prop:open={true}
      data-testid="agreements-modal"
    >
      <Switch>
        <Match when={view() === "form"}>
          <h2 slot="modal-dialog-header" data-testid="agreements-heading">
            Before you proceed
          </h2>
          <div slot="modal-dialog-body" data-testid="agreements-message">
            In order to use featured dealer, someone must sign the updated terms
            and conditions agreement via email.
            <div class="space-y-FsSizing3x pt-FsSizing3x">
              <fd-mfe-text-field
                prop:required={true}
                prop:type="text"
                prop:pattern="^[a-zA-Z \-']+$"
                prop:patternMismatchMessage="Only letters, spaces, hyphens, and apostrophes allowed"
                prop:value={name()}
                onInput={handleNameInput}
                onChange={() => publishAgreementsNameInputChange(name())}
                data-testid="agreement-name-input"
              >
                Name
              </fd-mfe-text-field>
              <fd-mfe-text-field
                prop:required={true}
                prop:type="email"
                prop:pattern="^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$"
                prop:patternMismatchMessage="Please enter a valid email address"
                prop:placeholder="person@example.com"
                prop:value={email()}
                onInput={handleEmailInput}
                onChange={() => publishAgreementsEmailInputChange(email())}
                data-testid="agreement-email-input"
              >
                Email
              </fd-mfe-text-field>
            </div>
          </div>
          <fd-mfe-modal-dialog-footer slot="modal-dialog-footer">
            <fd-mfe-button
              prop:disabled={!isFormValid()}
              onClick={onSubmitForm}
              data-testid="send-email-button"
            >
              Send email
            </fd-mfe-button>
            <fd-mfe-button
              onClick={() => {
                publishAgreementsBackClick();
                if (modalRef) props.onClose(modalRef);
              }}
              prop:rank="secondary"
              data-testid="no-thanks-button"
            >
              Back to marketing
            </fd-mfe-button>
          </fd-mfe-modal-dialog-footer>
        </Match>

        {/* TODO - discuss with design on method for how user can cleanly return back to marketing page after submitting email */}
        <Match when={view() === "confirmation"}>
          <h2 slot="modal-dialog-header">Check your email</h2>
          <div slot="modal-dialog-body">
            The updated terms and conditions agreement have been sent to{" "}
            {maskEmail(sanitizeEmail(email()))}
          </div>
          <fd-mfe-modal-dialog-footer slot="modal-dialog-footer">
            <fd-mfe-button onClick={onResendEmail}>Resend email</fd-mfe-button>
            <fd-mfe-button onClick={onTryAnotherEmail} prop:rank="secondary">
              Try another email
            </fd-mfe-button>
          </fd-mfe-modal-dialog-footer>
        </Match>

        <Match when={view() === "resent"}>
          <h2 slot="modal-dialog-header">Check your email</h2>
          <div slot="modal-dialog-body">
            The updated terms and conditions agreement have been sent to{" "}
            {maskEmail(sanitizeEmail(email()))}
          </div>
          <fd-mfe-modal-dialog-footer slot="modal-dialog-footer">
            <fd-mfe-button onClick={() => modalRef && props.onClose(modalRef)}>
              Refresh page
            </fd-mfe-button>
            <fd-mfe-button onClick={onTryAnotherEmail} prop:rank="secondary">
              Try another email
            </fd-mfe-button>
          </fd-mfe-modal-dialog-footer>
        </Match>
      </Switch>
    </fd-mfe-modal-dialog>
  );
};
